import * as React from 'react';

import { ALL_TOKENS_ITEM, TokenItem } from 'components/Tokenizer';
// @ts-ignore
import Typeahead from 'components/Typeahead';
import errorLogger from 'utils/errorLogger';
import { KeyboardEventHandler } from 'types/typescript/eventHandlers';

type Props = {
  // Function that renders the list of results
  listRenderer?: (items: TokenItem[], loading: boolean) => React.ReactNode;
  // Function that renders a result
  itemRenderer?: (
    item: TokenItem,
    highlighted: boolean,
    query: string,
    onSelect: () => void
  ) => React.ReactNode;
  // Function that renders a "Select all" item
  selectAllItemRenderer?: (highlighted: boolean, onSelectAll: () => void) => React.ReactNode;
  // Function that renders a no more items prompt
  noMoreItemsRenderer?: () => React.ReactNode;
  // Function that renders a no results prompt
  noResultsRenderer?: (query: string) => React.ReactNode;
  className?: string;
  children: React.ReactElement;
};

type DefaultProps = {
  // Function that takes a query and returns a Promise that resolves data
  // Note: There is an opportunity to create DataSource classes here.
  // (e.g., StaticDataSource, AsyncDataSource, PaginatedDataSource, etc)
  dataSource: (query?: string) => Promise<any[]>;
  // Function that returns a filter function to filter results
  dataFilterFactory: (query: string) => (datum: string) => boolean;
  // Show the list of results inline rather than in a layer
  inline: boolean;
  // Callback when an item is selected
  onSelect: (_datum: any) => any;
  // Callback when the "Select all" item is selected
  onSelectAll: (data: TokenItem[]) => void;
};

type State = {
  query: string;
  results: any[];
  loading: boolean;
};

class DeprecatedTypeahead extends React.Component<Props & DefaultProps, State> {
  static defaultProps: DefaultProps = {
    dataSource: () => Promise.resolve([]),
    dataFilterFactory: (query: string) => {
      return datum => datum === query;
    },
    inline: false,
    onSelect: _datum => {},
    onSelectAll: () => {},
  };

  state = {
    query: '',
    results: [],
    loading: false,
  };

  componentWillMount() {
    if (this.props.inline) {
      this.loadData();
    }
  }

  data: any[] = [];

  setQuery(query: string = '') {
    const trimmedQuery = query.trim();
    this.setState(
      {
        query: trimmedQuery,
        results: this.filterDataWithQuery(trimmedQuery),
      },
      () => this.loadData(trimmedQuery)
    );
  }

  handleInputFocus() {
    if (!this.state.loading && this.data.length === 0) {
      this.loadData(this.state.query);
    }
  }

  handleKeyDown: KeyboardEventHandler = event => {
    (this.refs.typeahead as any).handleKeyDown(event);
  };

  updateResults() {
    const results = this.filterDataWithQuery(this.state.query);
    this.setState({
      loading: false,
      results,
    });
  }

  filterDataWithQuery(query: string) {
    const data = this.data.filter(this.props.dataFilterFactory(query));

    if (!query) {
      if (this.props.selectAllItemRenderer && data.length > 1) {
        // XXX refactoring types but this is weird
        data.unshift(ALL_TOKENS_ITEM);
      }
    }

    return data;
  }

  loadData(query: string = '') {
    if (query === '' && this.data.length > 0) {
      // the query is empty, but we're not waiting on data
      // This can be further improved by not loading whenever the query truncated by delete key
      this.updateResults();
      return;
    }

    this.setState({ loading: true });
    this.props
      .dataSource(query)
      .then(data => {
        this.data = data;
        this.updateResults();
      })
      .catch(errorLogger('Data source query failure'));
  }

  render() {
    return (
      <Typeahead
        ref="typeahead"
        className={this.props.className}
        inline={this.props.inline}
        query={this.state.query}
        results={this.state.results}
        itemRenderer={this.props.itemRenderer}
        listRenderer={this.props.listRenderer}
        selectAllItemRenderer={this.props.selectAllItemRenderer}
        noMoreItemsRenderer={this.props.noMoreItemsRenderer}
        noResultsRenderer={this.props.noResultsRenderer}
        onSelect={this.props.onSelect}
        loading={this.state.loading}
        onSelectAll={() => this.props.onSelectAll(this.data)}
        sendToFew={false}>
        {React.cloneElement(this.props.children, {
          onFocus: () => this.handleInputFocus(),
        })}
      </Typeahead>
    );
  }
}

export default DeprecatedTypeahead;
